import { z } from 'zod'

import { NULL_UUID_ID } from '@/constants'
import type {
  ProductSupplierDto,
  StoreModel,
  StoreRequest,
} from '@/pages/Exchanges/tabs/StockImports/types/stockImports.types'
import type { OptionType, PriceSource, ProductSupplierShortDto } from '@/types'
import { digitsMask } from '@/utils/forms/inputMasks'

import { isStoreUnassigned } from './storeFormHelpers'

const STORE_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
const STORE_TIME_LENGTH = 5
const STORE_TIME_SECONDS = ':00'

export const STORE_PRICE_SOURCE_LABELS: Record<PriceSource, string> = {
  ERP: 'из УТ',
  IMPORT_FILE: 'из файла поставщика',
}

export const STORE_DAYS_MASK = digitsMask

export const STORE_TIME_MASK = [/\d/, /\d/, ':', /\d/, /\d/]

const optionSchema = z.object({ value: z.string(), label: z.string() })

const etaDaysSchema = z.string().min(1, 'Укажите срок')

export const storeCreationSchema = z
  .object({
    supplier: optionSchema.nullable().catch(null),
    description: z.string().trim().min(1, 'Укажите наименование'),
    address: z.string().trim(),
    contactPersons: z.string().trim(),
    comment: z.string().trim(),
    etaMinDays: etaDaysSchema,
    etaMaxDays: etaDaysSchema,
    etaCutoffTime: z
      .string()
      .min(1, 'Укажите время отсечки')
      .refine(
        (value) => value === '' || STORE_TIME_PATTERN.test(value),
        'Часы от 00 до 23, минуты от 00 до 59'
      ),
    isRemote: z.boolean(),
    isActive: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const { etaMinDays, etaMaxDays } = values
    if (etaMinDays && etaMaxDays && Number(etaMinDays) > Number(etaMaxDays)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['etaMinDays'],
        message: 'Не больше максимального срока',
      })
    }
  })

export type StoreCreationFormType = z.infer<typeof storeCreationSchema>

const toSupplierOption = (
  supplier: Pick<ProductSupplierShortDto, 'id' | 'description'>
): OptionType => ({ value: supplier.id, label: supplier.description })

const resolveDefaultSupplier = (
  store: StoreModel | null,
  supplier: ProductSupplierDto | null
): OptionType | null => {
  if (store) {
    return store.productSupplierShortDto && !isStoreUnassigned(store)
      ? toSupplierOption(store.productSupplierShortDto)
      : null
  }
  return supplier ? toSupplierOption(supplier) : null
}

export const buildStoreFormDefaults = (
  store: StoreModel | null,
  supplier: ProductSupplierDto | null
): StoreCreationFormType => ({
  supplier: resolveDefaultSupplier(store, supplier),
  description: store?.description ?? '',
  address: store?.address ?? '',
  contactPersons: store?.contactPersons ?? '',
  comment: store?.comment ?? '',
  etaMinDays: store ? String(store.etaMinDays) : '',
  etaMaxDays: store ? String(store.etaMaxDays) : '',
  etaCutoffTime: store ? store.etaCutoffTime.slice(0, STORE_TIME_LENGTH) : '',
  isRemote: store?.isRemote ?? false,
  isActive: store?.isActive ?? true,
})

export const buildStoreSupplierOptions = (
  suppliers: ProductSupplierDto[],
  supplier: ProductSupplierDto | null,
  store: StoreModel | null
): OptionType[] => {
  const options = new Map<string, OptionType>()
  suppliers.forEach((item) => options.set(item.id, toSupplierOption(item)))
  if (supplier) options.set(supplier.id, toSupplierOption(supplier))
  if (store?.productSupplierShortDto && !isStoreUnassigned(store)) {
    options.set(
      store.productSupplierShortDto.id,
      toSupplierOption(store.productSupplierShortDto)
    )
  }
  return Array.from(options.values())
}

export const toStoreRequest = (
  values: StoreCreationFormType,
  storeStatusId: string
): StoreRequest => ({
  description: values.description,
  address: values.address,
  contactPersons: values.contactPersons,
  comment: values.comment,
  isRemote: values.isRemote,
  isActive: values.isActive,
  storeStatus: storeStatusId,
  productSupplierId: values.supplier ? values.supplier.value : NULL_UUID_ID,
  etaMinDays: Number(values.etaMinDays),
  etaMaxDays: Number(values.etaMaxDays),
  etaCutoffTime: `${values.etaCutoffTime}${STORE_TIME_SECONDS}`,
})
